import type { IChallenge } from "@bbj-types/challenge";
import type { ISocialOffer } from "@bbj-types/offer";
import { ChipAccordion } from "@data-display/ChipAccordion";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EuroIcon from "@mui/icons-material/Euro";
import { Chip, List, ListItem, Typography } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { useI18nContext } from "@root/utils/organisation";
import { IconSVG } from "@styleguide";
import { useMemo } from "react";
import { InfoCard, infoCardClasses } from "../../../offers/Document/styles";

interface IDrawerSocialOfferBodyTypeProps {
  offer: ISocialOffer;
  challenges: IChallenge[];
}

export const DrawerSocialOfferBodyType = ({ offer, challenges }: IDrawerSocialOfferBodyTypeProps) => {
  const { settingsV2 } = useGlobalContext();
  const { timeZone } = useI18nContext();
  const isPermanent = !offer.serviceStartDate && !offer.serviceEndDate;
  const isExpired = offer.serviceEndDate && offer.serviceEndDate < new Date();
  const serviceDate = isExpired
    ? "Offre expirée"
    : isPermanent
      ? "Permanente"
      : `${
          offer.serviceStartDate?.toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            timeZone,
          }) ?? "..."
        } - ${
          offer.serviceEndDate?.toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            timeZone,
          }) ?? "..."
        }`;

  const matchedChallenges = useMemo((): readonly IChallenge[] => {
    const offerTags = offer.tags;
    if (!offerTags?.length) {
      return [];
    }
    return challenges
      .filter((challenge) => challenge.tags?.some((tag) => offerTags.includes(tag)))
      .map((challenge) => ({
        ...challenge,
        tags: challenge.tags.filter((tag) => offerTags.includes(tag)),
      }));
  }, [offer, challenges]);

  return (
    <InfoCard className={infoCardClasses.root}>
      <Typography variant="subtitle2SemiBold">Type de service</Typography>
      <Chip
        icon={<CalendarTodayIcon />}
        color="blue"
        sx={(theme) => ({ background: isExpired ? theme.palette.gray.dark : "" })}
        label={serviceDate}
        size="small"
      />
      {matchedChallenges.map((challenge) => (
        <ChipAccordion
          key={challenge._id}
          icon={<IconSVG icon={challenge.icon} />}
          color="primary"
          label={challenge.name}
          size="small"
        >
          <List sx={{ alignSelf: "start", listStyleType: "disc", pt: 0, pb: 0, pl: 2, color: "primary.main" }}>
            {challenge.tags.map((tag) => {
              if (settingsV2.statusSocialTag?.values.find((v) => v.value === tag)) {
                return (
                  <ListItem key={tag} sx={{ display: "list-item", p: 0, pb: 0.5 }}>
                    <Typography color="primary" variant="body2">
                      {settingsV2.statusSocialTag?.values.find((v) => v.value === tag)?.label ?? tag}
                    </Typography>
                  </ListItem>
                );
              }
            })}
          </List>
        </ChipAccordion>
      ))}
      {offer.financingModes?.map((financingMode) => {
        return (
          <Chip
            key={financingMode}
            icon={<EuroIcon />}
            color="yellow"
            label={settingsV2.financingModes?.values.find((v) => v.value === financingMode)?.label ?? financingMode}
            size="small"
          />
        );
      })}
    </InfoCard>
  );
};
